import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { merge, ReplaySubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { PriceListManagementRepository } from '@data-layer/repositories';
import { ManagementPriceListCombinedDetailedModel } from '@data-layer/models/management';
import { enumVoyageStatus } from '@data-layer/enums/lcl';
import { finalize, takeUntil } from 'rxjs/operators';
import { DateHelper } from '@helpers/date.helper';

@Component({
  selector: 'create-voyage-form',
  templateUrl: './create-voyage-form.component.html',
  styleUrls: ['./create-voyage-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateVoyageFormComponent implements OnInit, OnDestroy {
  @Input() public searchRoute: Function;
  @Input() public fg: UntypedFormGroup;

  public errors: IErrorForm = {
    required: 'FORM_ERROR.REQUIRED',
    noPricelistMatch: 'FORM_ERROR.NOT_MATCH_AGENT_PRICELISTS',
  };
  public VOYAGE_STATUSES: Array<{ label: string; value: number }> = [];

  public combinedPricelists: ManagementPriceListCombinedDetailedModel[] = [];
  public combinedPricelistsNoMargin: boolean;
  public combinedPricelistsHasColoadingOriginError: boolean;
  public combinedPricelistsHasColoadingDestinationError: boolean;
  public combinedPricelistsHasFullContainerDestinationError: boolean;
  public isLoadingActivePriceLists: boolean;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private translateService: TranslateService,
    private priceListService: PriceListManagementRepository,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.getVoyageStatuses();

    this.fg.controls['estimatedDepartureTime'].valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.clearPriceLists();
      this.getActivePriceLists();
    });
    this.fg.controls['route'].valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.clearPriceLists();
      this.getActivePriceLists();
    });

    this.fg
      .get('combinedPricelistId')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((priceListId: ManagementPriceListCombinedDetailedModel) => {
        this.combinedPricelistsNoMargin = false;
        this.combinedPricelistsHasColoadingOriginError = false;
        this.combinedPricelistsHasColoadingDestinationError = false;
        this.combinedPricelistsHasFullContainerDestinationError = false;

        if (!priceListId) {
          return;
        }

        this.combinedPricelistsNoMargin = !this.combinedPricelists.find(
          (priceListItem: ManagementPriceListCombinedDetailedModel) => priceListItem.id === priceListId.id && priceListItem.hasMargins,
        );
        this.combinedPricelistsHasColoadingOriginError = !this.combinedPricelists.find(
          (priceListItem: ManagementPriceListCombinedDetailedModel) =>
            priceListItem.id === priceListId.id && priceListItem.hasColoadingCfsAtOriginPort,
        );
        this.combinedPricelistsHasColoadingDestinationError = !this.combinedPricelists.find(
          (priceListItem: ManagementPriceListCombinedDetailedModel) =>
            priceListItem.id === priceListId.id && priceListItem.hasColoadingCfsAtDestinationPort,
        );
        this.combinedPricelistsHasFullContainerDestinationError = !this.combinedPricelists.find(
          (priceListItem: ManagementPriceListCombinedDetailedModel) =>
            priceListItem.id === priceListId.id && priceListItem.hasFullContainerCfsAtDestinationPort,
        );

        this.fg.get('combinedPricelistId').setErrors(this.combinedPricelistsNoMargin ? { required: true } : null);
        if (this.combinedPricelists?.length) {
          this.fg
            .get('combinedPricelistId')
            .setErrors(
              this.combinedPricelistsHasColoadingOriginError ||
                this.combinedPricelistsHasColoadingDestinationError ||
                this.combinedPricelistsHasFullContainerDestinationError
                ? { required: true }
                : null,
            );
        }
      });

    merge(this.fg.get('route').valueChanges, this.fg.get('estimatedDepartureTime').statusChanges)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.fg.get('combinedPricelistId').setErrors(null);
        this.fg.get('combinedPricelistId').reset();
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private clearPriceLists(): void {
    this.fg.controls['combinedPricelistId'].setValue(null);
  }

  private getActivePriceLists(): void {
    if (this.fg.controls['route'].value && this.fg.controls['estimatedDepartureTime'].value) {
      this.isLoadingActivePriceLists = true;

      this.priceListService
        .getActivePriceLists(
          this.fg.controls['route'].value.departurePort.id,
          this.fg.controls['route'].value.arrivalPort.id,
          this.fg.controls['estimatedDepartureTime'].value,
        )
        .pipe(
          finalize(() => (this.isLoadingActivePriceLists = false)),
          takeUntil(this.destroy$),
        )
        .subscribe((combinedPricelists: ManagementPriceListCombinedDetailedModel[]) => {
          this.combinedPricelists = this.filterByRoute(combinedPricelists);

          this.cdr.markForCheck();
        });
    }
  }

  private getVoyageStatuses(): void {
    this.VOYAGE_STATUSES = Object.keys(enumVoyageStatus)
      .filter((item: string) => Number(item) > -1)
      .map((item: string) => ({
        label: this.translateService.instant('VOYAGE_STATUS.' + enumVoyageStatus[item]),
        value: Number(item),
      }));
  }

  private filterByRoute(pricelists: ManagementPriceListCombinedDetailedModel[]): ManagementPriceListCombinedDetailedModel[] {
    const from: number = this.fg.controls['route'].value.departurePort.id;
    const to: number = this.fg.controls['route'].value.arrivalPort.id;

    return pricelists.filter((item: ManagementPriceListCombinedDetailedModel) => {
      return item.originPort.id === from && item.destinationPort.id === to;
    });
  }

  public get todayDate(): string {
    return DateHelper.now;
  }

  public get etaMinDate(): string {
    return this.fg.controls['estimatedDepartureTime'].value ? this.fg.controls['estimatedDepartureTime'].value : DateHelper.now;
  }

  public get isShowPriceListETDError(): boolean {
    return !this.combinedPricelists?.length && !!this.fg.get('estimatedDepartureTime').value && !this.isLoadingActivePriceLists;
  }

  public get agentName(): string {
    return this.fg.get('combinedPricelistId')?.value?.destinationAgent?.name || '';
  }

  public get containerError(): string {
    const result: string[] = [];

    if (this.combinedPricelistsHasColoadingOriginError || this.combinedPricelistsHasColoadingDestinationError) {
      result.push(this.translateService.instant('LABELS.COLOADING'));
    }
    if (this.combinedPricelistsHasFullContainerDestinationError) {
      result.push(this.translateService.instant('LABELS.FULL_CONTAINER'));
    }

    return result.join(', ');
  }

  public get portName(): string {
    let result: string = '';

    if (this.combinedPricelistsHasColoadingOriginError) {
      result += `${this.fg.get('route').value?.departurePort?.code} ${this.fg.get('route').value?.departurePort?.description}`;
    }

    if (this.combinedPricelistsHasColoadingDestinationError) {
      result += `${this.fg.get('route').value?.arrivalPort?.code} ${this.fg.get('route').value?.arrivalPort?.description}`;
    }

    if (this.combinedPricelistsHasFullContainerDestinationError) {
      result += `${this.fg.get('route').value?.arrivalPort?.code} ${this.fg.get('route').value?.arrivalPort?.description}`;
    }

    return result;
  }

  public get routeName(): string {
    return this.fg.get('route').value?.displayName;
  }

  public get hasPriceListError(): boolean {
    return (
      this.combinedPricelistsNoMargin ||
      this.isShowPriceListETDError ||
      this.combinedPricelistsHasColoadingOriginError ||
      this.combinedPricelistsHasColoadingDestinationError ||
      this.combinedPricelistsHasFullContainerDestinationError
    );
  }
}
